import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Tooltip,
  Typography,
  Divider,
  Box,
  CardActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import dayjs from "dayjs";
import AuthContext from "../Context/AuthProvider";
import AddRefundDialog from "./AddRefundDialog";
import { getBillById, addRefund, removeRefund } from "../API/BillingApi";
import { refundPaymentModes as paymentModes } from "../Utility/Constants";
import { numberToWords } from "../Utility/Utility";
import ConfirmationDialog from "../common/ConfirmationDialog";
import { convertUTCToTimeZoneDateString } from "../Utility/DateTimeUtility";

const RefundTableSection = ({
  billId,
  showSnackbar,
  onRefundDataChange,
  isSmallTablet,
  onTotalRefundChange,
}) => {
  const { auth } = useContext(AuthContext);
  const theme = useTheme();

  const [refunds, setRefunds] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newRefund, setNewRefund] = useState({
    amount: "",
    refundMode: "",
    notes: "",
    refundedTo: "",
  });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [refundToDeleteId, setRefundToDeleteId] = useState(null);

  const fetchRefunds = async () => {
    if (!billId) return;
    setLoading(true);
    try {
      const result = await getBillById(auth, billId);
      if (result.isSuccess && result.data?.refunds) {
        setRefunds(result.data.refunds);
      } else {
        const msg =
          typeof result.data === "string"
            ? result.data
            : result.data?.title || "Failed to fetch refunds.";
        showSnackbar?.(msg, "error");
      }
    } catch (error) {
      console.error("Error fetching refunds:", error);
      showSnackbar?.("Error fetching refunds.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefunds();
  }, [billId, auth, showSnackbar]);

  const handleAddRefund = async () => {
    if (!newRefund.amount || !newRefund.refundMode) {
      showSnackbar?.(
        "Please enter refund amount and select a refund mode.",
        "warning"
      );
      return;
    }

    setIsSaving(true);
    try {
      const response = await addRefund(
        auth,
        billId,
        parseFloat(newRefund.amount),
        newRefund.refundMode,
        newRefund.notes,
        newRefund.refundedTo
      );

      if (response.isSuccess) {
        setRefunds((prev) => [...prev, response.data]);
        setOpenDialog(false);
        showSnackbar?.("Refund added successfully!", "success");
        setNewRefund({ amount: "", refundMode: "", notes: "", refundedTo: "" });
        onRefundDataChange?.();
      } else {
        const msg =
          typeof response.data === "string"
            ? response.data
            : response.data?.title || "Failed to add refund.";
        showSnackbar?.(msg, "error");
      }
    } catch (error) {
      console.error("Error adding refund:", error);
      showSnackbar?.("An error occurred while adding refund.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRefund = (refundId) => {
    setRefundToDeleteId(refundId);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteRefund = async () => {
    if (!refundToDeleteId) return;

    try {
      const response = await removeRefund(auth, refundToDeleteId);
      if (response.isSuccess) {
        showSnackbar("Refund removed successfully!", "success");
        await fetchRefunds();
        onRefundDataChange?.();
      } else {
        showSnackbar(response.data || "Failed to remove refund.", "error");
      }
    } catch (error) {
      console.error("Error removing refund:", error);
      showSnackbar("An error occurred while removing refund.", "error");
    } finally {
      setOpenDeleteDialog(false);
      setRefundToDeleteId(null);
    }
  };

  const totalRefundAmount = useMemo(
    () => refunds.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    [refunds]
  );

  const currentTotalRefundFormatted = totalRefundAmount.toFixed(2);
  const currentTotalRefundInWords = numberToWords(currentTotalRefundFormatted);

  useEffect(() => {
    onTotalRefundChange?.(parseFloat(currentTotalRefundFormatted));
  }, [currentTotalRefundFormatted, onTotalRefundChange]);

  const columns = [
    { field: "mode", headerName: "Refund Mode", width: 150 },
    {
      field: "refundedTo",
      headerName: "Refunded To",
      flex: 1,
      minWidth: 150,
      renderCell: (params) =>
        params.row.id === "TOTAL" ? (
          <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
            {params.value}
          </Typography>
        ) : (
          params.value
        ),
    },
 {
  field: "amount",
  headerName: "Amount",
  width: 150,
  align: 'right',
  headerAlign: 'right',
  renderCell: (params) =>
    params.row.id === "TOTAL" ? (
      <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
        Rs {currentTotalRefundFormatted}
      </Typography>
    ) : (
      `Rs ${Number(params.row.amount).toFixed(2)}`
    ),
},
    { field: "createdByUserName", headerName: "Refunded By", width: 150 },
  
    {
       field: "refundDateTime",
      headerName: "Refund Date",
      width: 150,
        renderCell: (params) =>
          params.value
            ? convertUTCToTimeZoneDateString(
                params.value,
                auth?.selectedFacility?.timeZone
              )
            : "",
      },
   
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) =>
        params.row.id === "TOTAL" ? null : (
          <Tooltip title="Remove" arrow>
            <IconButton onClick={() => handleDeleteRefund(params.row.id)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
    },
  ];

  const renderCards = (data) => {
    if (!data || data.length === 0) {
      return (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ textAlign: "center", p: 2 }}
        >
          No refunds found.
        </Typography>
      );
    }

    return (
      <Box sx={{ p: 1 }}>
        {data
          .filter((row) => row.id !== "TOTAL")
          .map((row) => (
            <Card
              key={row.id}
              variant="outlined"
              sx={{ mb: 1.5, borderRadius: 2 }}
            >
              <CardContent>
                <Typography variant="h5">
                  <strong>Refund</strong>:{" "}
                  <strong>Rs {Number(row.amount).toFixed(2)}</strong>
                </Typography>
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="body2">Mode: {row.mode}</Typography>
                <Typography variant="body2">
                  Refunded To: {row.refundedTo}
                </Typography>
                <Typography variant="body2">
                  Refunded By: {row.createdByUserName}
                </Typography>
               
                  <Typography variant="body2">
                  Date:{" "}
                  {convertUTCToTimeZoneDateString(
                    row.refundDateTime,
                    auth?.selectedFacility?.timeZone  // Add selectedFacility
                  )}
                </Typography>
                              
                {row.notes && (
                  <Typography variant="body2">Notes: {row.notes}</Typography>
                )}
              </CardContent>
              <CardActions sx={{ justifyContent: "flex-end", paddingTop: 0 }}>
                <Tooltip title="Remove" arrow>
                  <IconButton
                    onClick={() => handleDeleteRefund(row.id)}
                    color="error"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </CardActions>
            </Card>
          ))}
      </Box>
    );
  };

  return (
    <>
      <Card variant="outlined" sx={{ width: "100%", borderStyle: "none" }}>
        <CardHeader
          subheader="Refunds"
          subheaderTypographyProps={{ variant: "h6" }}
          sx={{ paddingY: "10px" }}
          action={
            <Tooltip title="New Refund" arrow>
              <IconButton
                color="secondary"
                size="small"
                onClick={() => setOpenDialog(true)}
              >
                <AddRoundedIcon />
              </IconButton>
            </Tooltip>
          }
        />
        <CardContent
          sx={{ height: isSmallTablet ? "auto" : "300px", paddingTop: "0px" }}
        >
          {isSmallTablet ? (
            renderCards(refunds)
          ) : (
            <DataGrid
              sx={{
                height: "100%",
                "& .MuiTablePagination-toolbar": {
                  minHeight: "unset",
                  alignItems: "center",
                  paddingTop: "0 !important",
                  paddingBottom: "0 !important",
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                  {
                    margin: 0,
                  },
              }}
              initialState={{ density: "compact" }}
              rows={refunds}
              columns={columns}
              getRowId={(row) => row.id}
              disableColumnFilter
              disableColumnMenu
              disableColumnSorting
              loading={loading} // ✅ Skeleton rows
              slotProps={{
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
              }}
              hideFooter={false}
              slots={{
                footer: () => (
                  <Box
                    sx={{
                      height: "60px",
                      backgroundColor: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      px: 2,
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      {totalRefundAmount === 0
                        ? "Total Refund Amount (INR Zero only)"
                        : `Total Refund Amount (INR ${currentTotalRefundInWords} only)`}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      Rs {currentTotalRefundFormatted}
                    </Typography>
                  </Box>
                ),
              }}
            />
          )}
        </CardContent>
      </Card>

      <AddRefundDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSave={handleAddRefund}
        newRefund={newRefund}
        setNewRefund={setNewRefund}
        refundPaymentModes={paymentModes}
        isSaving={isSaving}
      />

      <ConfirmationDialog
        title="Delete Refund"
        statement="Are you sure you want to delete this refund? This action cannot be undone."
        okButtonColor="error"
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={confirmDeleteRefund}
      />
    </>
  );
};

export default RefundTableSection;
